import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
  Alert,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";
import {
  getFormDirection,
  getRtlSelectProps,
  getRtlTextFieldProps,
} from "../utils/rtlForm";

import JalaliDateField from "../Components/JalaliDateField";
const ExpenseAdd = ({ open, onClose, token }) => {
  const { t, i18n } = useTranslation();
  const formDirection = getFormDirection(i18n);
  const { fieldDirection, fieldTextAlign } = formDirection;
  const selectProps = getRtlSelectProps(formDirection);
  const { handleAdd, loading, resetForm } = useAdd("shop/expenses", token);

  const { data: categoriesData, loading: categoryLoading } = useFetchObjects(
    ["expense-categories"],
    "shop/expense-categories",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies?page_size=all",
    token
  );

  const categories = categoriesData?.results || categoriesData || [];
  const currencies = currenciesData?.results || currenciesData || [];

  const [selectedCategory, setSelectedCategory] = useState("");
  const [expenseBy, setExpenseBy] = useState("");
  const [amount, setAmount] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [notes, setNotes] = useState("");

  const selectedCurrencyDetails = useMemo(
    () => currencies.find((cur) => String(cur.id) === String(selectedCurrency)) || null,
    [currencies, selectedCurrency]
  );
  const selectedShopId = selectedCurrencyDetails?.shop || "";

  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "expense-add", selectedShopId],
    selectedShopId ? `shop/accounts/?page_size=all&shop=${selectedShopId}` : "",
    token
  );
  const accounts = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];
  const selectedAccount = useMemo(
    () => accounts.find((acc) => String(acc.id) === String(selectedAccountId)) || null,
    [accounts, selectedAccountId]
  );
  const sameCurrencyAccounts = useMemo(
    () => accounts.filter((acc) => String(acc.currency) === String(selectedCurrency)),
    [accounts, selectedCurrency]
  );

  useEffect(() => {
    if (!selectedCurrency) {
      setSelectedAccountId("");
      return;
    }
    if (accounts.length === 0) {
      setSelectedAccountId("");
      return;
    }
    const exists = accounts.some((acc) => String(acc.id) === String(selectedAccountId));
    if (!exists) {
      const preferred = sameCurrencyAccounts[0] || accounts[0];
      setSelectedAccountId(preferred ? String(preferred.id) : "");
    }
  }, [selectedCurrency, accounts, sameCurrencyAccounts, selectedAccountId]);

  const { data: directRateData } = useFetchObjects(
    ["expense-rate-direct", selectedShopId, selectedCurrency, selectedAccount?.currency],
    selectedShopId && selectedCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${selectedCurrency}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    ["expense-rate-reverse", selectedShopId, selectedCurrency, selectedAccount?.currency],
    selectedShopId && selectedCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${selectedAccount.currency}&to_currency=${selectedCurrency}`
      : "",
    token
  );

  const exchangeRatePreview = useMemo(() => {
    if (!selectedCurrency) return "";
    const accountCurrencyId = selectedAccount?.currency ?? selectedCurrency;
    if (String(selectedCurrency) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [selectedCurrency, selectedAccount, directRateData, reverseRateData]);

  useEffect(() => {
    setExchangeRate(exchangeRatePreview);
  }, [exchangeRatePreview]);

  const convertedAmount = useMemo(() => {
    const nAmount = Number(amount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [amount, exchangeRate]);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("category", selectedCategory);
    formData.append("expense_by", expenseBy);
    formData.append("amount", amount);
    formData.append("currency", selectedCurrency);
    formData.append("account", selectedAccountId);
    formData.append("exchange_rate", exchangeRate);
    formData.append("payment_date", paymentDate);
    formData.append("notes", notes);
    handleAdd(formData);
  };

  useEffect(() => {
    if (resetForm) {
      setSelectedCategory("");
      setExpenseBy("");
      setAmount("");
      setSelectedCurrency("");
      setSelectedAccountId("");
      setExchangeRate("");
      setPaymentDate("");
      setNotes("");
      onClose();
    }
  }, [resetForm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" dir={fieldDirection}>
      <DialogTitle>{t("expense.addExpense")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1, direction: fieldDirection }}>
          <TextField
            select
            margin="dense"
            label={t("expense.selectCategory")}
            fullWidth
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {categoryLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (categories || []).map((cat) => (
                <MenuItem key={cat.id} value={String(cat.id)}>
                  {cat.name}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("expense.expenseBy")}
            fullWidth
            required
            value={expenseBy}
            onChange={(e) => setExpenseBy(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />

          <TextField
            margin="dense"
            label={t("expense.amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />

          <TextField
            select
            margin="dense"
            label={t("expense.selectCurrency")}
            fullWidth
            required
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (currencies || []).map((cur) => (
                <MenuItem key={cur.id} value={String(cur.id)}>
                  {cur.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("expense.account", "Account")}
            fullWidth
            required
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            disabled={!selectedCurrency || accountLoading}
            SelectProps={selectProps}
            {...getRtlTextFieldProps(formDirection)}
          >
            {accountLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : accounts.length === 0 ? (
              <MenuItem value="" disabled>
                {t("expense.noAccount", "No accounts found")}
              </MenuItem>
            ) : (
              accounts.map((acc) => (
                <MenuItem key={acc.id} value={String(acc.id)}>
                  {acc.name} ({acc.currency_title || "-"})
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("expense.exchangeRate", "Exchange Rate")}
            type="number"
            fullWidth
            required
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            {...getRtlTextFieldProps(formDirection, {
              inputProps: { min: 0, step: "0.000001" },
            })}
          />

          <TextField
            margin="dense"
            label={t("expense.convertedAmount", "Converted Amount")}
            fullWidth
            value={convertedAmount}
            disabled
            {...getRtlTextFieldProps(formDirection)}
          />

          {!exchangeRatePreview && selectedCurrency && selectedAccountId && !exchangeRate && (
            <Alert severity="warning" sx={{ mt: 1, direction: fieldDirection, textAlign: fieldTextAlign }}>
              {t("expense.missingRate", "No exchange rate found for selected account currency. You can enter it manually.")}
            </Alert>
          )}

          <JalaliDateField mode="datetime"
            margin="dense"
            label={t("expense.paymentDate")}
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            {...getRtlTextFieldProps(formDirection, {
              InputLabelProps: { shrink: true },
            })}
          />

          <TextField
            margin="dense"
            label={t("expense.notes")}
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            {...getRtlTextFieldProps(formDirection)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("expense.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !expenseBy ||
            !amount ||
            !selectedCurrency ||
            !selectedAccountId ||
            !exchangeRate ||
            !paymentDate
          }
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("expense.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExpenseAdd;
